import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useAxios } from "@/composables/api.js";
import { useCompanyStore } from "@/stores/company.js";
import { useCampaignStore } from "@/stores/campaign.js";
import { useAppStore } from "@/stores/app.js";
import { useModulesStore } from "@/stores/modules.js";
import {
  getModuleRank,
  computeModuleScore,
} from "@/composables/computeResults.js";
import { dateIsBefore } from "@/composables/utils.js";
import { catchError } from "@/composables/error.js";
import { useToast } from "vue-toastification";
import { getError } from "@/composables/error.js";

export const useParticipantStore = defineStore("participant", () => {
  // Init stores
  const companyStore = useCompanyStore();
  const campaignStore = useCampaignStore();
  const appStore = useAppStore();
  const { modules } = useModulesStore();

  const participants = ref([]);

  const currentParticipant = ref({});

  const campaignParticipants = computed(() => {
    if (!appStore.currentRoute.params.campaignId) return null;

    return participants.value.filter(
      (participant) =>
        participant.campaign_id === appStore.currentRoute.params.campaignId
    );
  });

  const toActiveParticipants = computed(() => {
    if (!campaignParticipants.value) return null;

    return campaignParticipants.value.filter(
      (participant) => participant.activated === false
    );
  });

  const finishedParticipants = computed(() => {
    if (!campaignParticipants.value) return null;

    const isv2Campaign = campaignStore.currentCampaign.version === 2;
    const participantsList = campaignParticipants.value.filter(
      (participant) =>
        participant.results &&
        (!isv2Campaign ||
          (participant.results.adaptability_score !== undefined &&
            participant.results.adaptability_score !== null))
    );

    return participantsList.sort(
      (a, b) => b.results.compatibility - a.results.compatibility
    );
  });

  const inProgressParticipants = computed(() => {
    if (!campaignParticipants.value) return null;

    const isv2Campaign = campaignStore.currentCampaign.version === 2;
    const participantsList = campaignParticipants.value.filter(
      (participant) =>
        participant.activated === true &&
        (!participant.results ||
          (isv2Campaign &&
            (participant.results.adaptability_score === undefined ||
              participant.results.adaptability_score === null)))
    );

    return participantsList.sort((a, b) => {
      if (a.candidate && !b.candidate) return -1;
      if (!a.candidate && b.candidate) return 1;

      if (a.state > 0 && b.state === 0) return -1;
      if (a.state === 0 && b.state > 0) return 1;

      if (a.progress !== b.progress) return b.progress - a.progress;

      return dateIsBefore(a.creation_time, b.creation_time) ? 1 : -1;
    });
  });

  async function addParticipants(
    companyId,
    workspaceId,
    campaignId,
    newParticipants
  ) {
    try {
      const { data } = await useAxios.post(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaignId}/participants`,
        { participants: newParticipants }
      );
      const toast = useToast();

      if (
        data.duplicate_participants &&
        Array.isArray(data.duplicate_participants) &&
        data.duplicate_participants.length > 0
      ) {
        const mails = data.duplicate_participants.reduce((acc, curr) => {
          if (acc !== "") return acc + ", " + curr.email;
          else return acc + curr.email;
        }, "");
        toast.error(
          `${getError(
            data.duplicate_participants.length > 1
              ? "ERROR_PROVIDED_EMAILS_ALREADY_USED"
              : "ERROR_PROVIDED_EMAIL_ALREADY_USED"
          )} ${mails}. ${getError(
            data.duplicate_participants.length > 1
              ? "ERROR_PROVIDED_EMAILS_ALREADY_USED_DETAIL"
              : "ERROR_PROVIDED_EMAIL_ALREADY_USED_DETAIL"
          )}`, {
          timeout: 10000,
        });
      }
      if (
        data.participants &&
        Array.isArray(data.participants) &&
        data.participants.length > 0
      ) {
        participants.value = participants.value.concat(data.participants);
      }

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getParticipants(companyId, workspaceId, campaignId) {
    try {
      const { data } = await useAxios.get(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaignId}/participants`
      );

      data.participants.forEach((participant) => {
        const alreadyIn = participants.value.find(
          (participantObj) =>
            participantObj.participant_id === participant.participant_id
        );

        if (!alreadyIn)
          participants.value = [...participants.value, participant];
      });

      participants.value = participants.value.sort((a, b) => {
        return dateIsBefore(a.creation_time, b.creation_time) ? 1 : -1;
      });

      const campaignIndex = campaignStore.campaigns.findIndex(
        (campaign) => campaign.campaign_id === campaignId
      );

      campaignStore.campaigns[campaignIndex].resultsAreComputed = false;

      campaignStore.campaigns[campaignIndex].participantsFetched = true;

      return participants.value;
    } catch (error) {
      return catchError(error);
    }
  }

  async function activateParticipants(
    companyId,
    workspaceId,
    campaignId,
    participantIds
  ) {
    try {
      const response = await useAxios.put(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaignId}/participants`,
        { participants: participantIds }
      );

      participantIds.forEach((participant) => {
        const obj = participants.value.find(
          (elt) => elt.participant_id === participant
        );

        if (obj) obj.activated = true;
      });

      companyStore.currentCompany.credits.available -= participantIds.length;

      return response;
    } catch (error) {
      return catchError(error);
    }
  }

  async function desactivateParticipants(
    companyId,
    workspaceId,
    campaignId,
    participantIds
  ) {
    try {
      const response = await useAxios.put(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaignId}/participants/activations`,
        { participants: participantIds }
      );

      participantIds.forEach((participant) => {
        const obj = participants.value.find(
          (elt) => elt.participant_id === participant
        );

        obj.activated = false;
      });

      companyStore.currentCompany.credits.available += participantIds.length;

      return response;
    } catch (error) {
      return catchError(error);
    }
  }

  async function moveParticipants(
    companyId,
    workspaceId,
    campaignId,
    participantIds,
    targetCampaign
  ) {
    try {
      const response = await useAxios.put(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaignId}/participants-move`,
        {
          participants: participantIds,
          target_campaign_id: targetCampaign,
        }
      );

      participantIds.forEach((elt) => {
        const index = participants.value.findIndex(
          (p) => p.participant_id === elt
        );
        participants.value[index].campaign_id = targetCampaign;
      });

      return response;
    } catch (error) {
      return catchError(error);
    }
  }

  async function deleteParticipants(
    companyId,
    workspaceId,
    campaignId,
    participantIds
  ) {
    try {
      const response = await useAxios.delete(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaignId}/participants`,
        { participants: participantIds }
      );

      participantIds.forEach((elt) => {
        const index = participants.value.findIndex(
          (p) => p.participant_id === elt
        );
        participants.value.splice(index, 1);
      });

      await companyStore.getCompanyCredits(companyId);

      return response;
    } catch (error) {
      return catchError(error);
    }
  }

  async function commentParticipant(
    companyId,
    spaceId,
    campaignId,
    participantId,
    notes
  ) {
    try {
      const response = await useAxios.put(
        `/companies/${companyId}/workspaces/${spaceId}/campaigns/${campaignId}/participants/${participantId}/comment`,
        {
          user_comment: notes,
        }
      );

      return response;
    } catch (error) {
      return catchError(error);
    }
  }

  async function sendReminder(
    participantId,
    companyId,
    workspaceId,
    campaignId
  ) {
    try {
      const { data } = await useAxios.post(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaignId}/participants/${participantId}/reminders`
      );

      if (data.sent) {
        const participantObj = participants.value.find(
          (elt) => elt.participant_id === participantId
        );

        participantObj.last_reminder_time = new Date().toISOString();
      }

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  function isV2_1_2(participant) {
    return participant?.results?.pb_solving_score >= 1000;
  }

  /**
 * Normalise the score for problem solving skill
 * @param score 
 */
  function normaliseScore(score) {
    const score_normalized = Math.min(score / (100 * 5 / 9) * 20, 20) + Math.max(0, (score - 100 * 5 / 9) / (100 * 4 / 9) * 80);
    return score_normalized
  }

  function computeResults() {
    let campaignScore = [];

    finishedParticipants.value.forEach((participant) => {
      const isParticipantV2_1_2 = isV2_1_2(participant);
      if (isParticipantV2_1_2) participant.results.pb_solving_score -= 1000;

      participant.results = Object.entries(participant.results)
        .filter(([result]) => {
          const resultFormat = result.split("_");
          resultFormat.pop();

          const data = modules.find(
            (elt) =>
              elt.id_v2 === resultFormat.join("_") ||
              elt.id === resultFormat.join("_")
          );

          return !data?.disabled;
        })
        .reduce((acc, [skill, value], index) => {
          if (skill.includes("_score") || skill.includes("MS_")) {
            const note = isNaN(value) ? value.note : value;

            acc[skill] = {
              rank: getModuleRank(
                note,
                campaignStore.currentCampaign.version,
                skill,
                campaignStore.currentCalibration,
                campaignStore.isV2_1(campaignStore.currentCampaign),
                isParticipantV2_1_2
              ).rank,
              note,
              score: Math.min(
                computeModuleScore(
                  note,
                  campaignStore.currentCampaign.version,
                  skill,
                  campaignStore.currentCalibration,
                  campaignStore.isV2_1(campaignStore.currentCampaign),
                  isParticipantV2_1_2
                ),
                100
              ),
            };

            if (skill !== "social_desire_score" && (skill !== "planning_score" || campaignStore.isV2_1(campaignStore.currentCampaign))) {

              if (skill === "pb_solving_score" && !campaignStore.isV2_1(campaignStore.currentCampaign) && !isV2_1_2(participant)) {
                acc[skill].score = normaliseScore(acc[skill].score);
              }

              if (campaignScore[index]) {
                campaignScore[index].score += acc[skill].score;
              } else {
                campaignScore[index] = {
                  id: skill,
                  score: acc[skill].score,
                };
              }

            }
          } else if (skill === "communication_style") {
            const values = {
              Assertive: "A",
              Conciliatory: "E",
            };

            acc[skill] = {
              rank: values[value] ?? "C",
              value,
            };
          } else if (skill === "learn_strategy_style") {
            const values = {
              Repeating: "A",
              Thinking: "E",
            };

            acc[skill] = {
              rank: values[value] ?? "C",
              value,
            };
          } else if (skill === "team_work_style") {
            const values = {
              Rigorous: "A",
              Spontaneous: "E",
            };

            acc[skill] = {
              rank: values[value] ?? "C",
              value,
            };
          } else if (skill === "leadership_style") {
            const values = {
              Participation: "A",
              Results: "E",
            };

            acc[skill] = {
              rank: values[value] ?? "C",
              value,
            };
          } else {
            acc[skill] = value;
          }

          return acc;
        }, {});
    });

    const sortedCampaignScore = campaignScore
      .filter((elt) => elt)
      .sort((a, b) => {
        const indexA = modules.findIndex(
          (elt) =>
            elt.id_v2 === a.id.replace("_score", "") ||
            elt.id === a.id.replace("_score", "")
        );

        const indexB = modules.findIndex(
          (elt) =>
            elt.id_v2 === b.id.replace("_score", "") ||
            elt.id === b.id.replace("_score", "")
        );

        return indexA - indexB;
      });

    campaignStore.currentCampaign.averageScores = sortedCampaignScore.map(
      (campaign_score) => {
        return Math.floor(
          campaign_score.score / finishedParticipants.value.length
        );
      }
    );

    computeCompatibility();

    const campaignIndex = campaignStore.campaigns.findIndex(
      (campaign) =>
        campaign.campaign_id === campaignStore.currentCampaign.campaign_id
    );

    campaignStore.campaigns[campaignIndex].resultsAreComputed = true;
  }

  function computeCompatibility(
    coefs_v1 = {
      MS_RDPB: 2,
      MS_GDSS: 2,
      MS_ADPT: 2,
    }
  ) {
    const getCoef = (skill) => {
      if (campaignStore.currentCampaign.version === 1) return 1;

      if (campaignStore.currentCampaign.skills_mandatory.includes(skill))
        return 3;
      if (campaignStore.currentCampaign.skills_important.includes(skill))
        return 2;
      if (campaignStore.currentCampaign.skills_secondary.includes(skill))
        return 1;

      return 0;
    };

    finishedParticipants.value.forEach(({ results }) => {
      let total, divider;

      // V2
      if (campaignStore.currentCampaign.version === 2) {
        total = Object.entries(results)
          .filter(([key]) => key.includes("_score"))
          .filter(([key, result]) => result.score !== -1)
          .reduce(
            (acc, [key, result]) =>
              acc + result.score * getCoef(key.split("_score")[0]),
            0
          );

        divider = Object.entries(results)
          .filter(([key]) => key.includes("_score"))
          .filter(([key, result]) => result.score !== -1)
          .reduce((acc, [key]) => acc + getCoef(key.split("_score")[0]), 0);
        // V1
      } else {
        total = Object.entries(results)
          .filter(([key]) => key.includes("MS_"))
          .reduce(
            (acc, [key, result]) => acc + result.score * coefs_v1[key],
            0
          );

        divider = Object.values(coefs_v1).reduce(
          (acc, current) => acc + current,
          0
        );
      }

      if (!total || !divider) return (results.compatibility = 50);

      results.compatibility = total / divider;
    });
  }

  function clear() {
    participants.value = [];
  }

  return {
    campaignParticipants,
    currentParticipant,
    participants,
    finishedParticipants,
    inProgressParticipants,
    toActiveParticipants,
    addParticipants,
    getParticipants,
    activateParticipants,
    desactivateParticipants,
    moveParticipants,
    deleteParticipants,
    computeResults,
    computeCompatibility,
    clear,
    sendReminder,
    commentParticipant,
  };
});
